import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Lock, Mail, User as UserIcon } from 'lucide-react';
import { Brand } from '@/components/Brand';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { useAuthStore, type AuthUser } from '@/stores/authStore';
import { useCartStore } from '@/stores/cartStore';
import type { Plan } from '@/features/billing/types';

interface AuthResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

type Tab = 'login' | 'register';

export function LoginPage() {
  const [params] = useSearchParams();
  const initialTab: Tab = params.get('tab') === 'register' ? 'register' : 'login';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const addToCart = useCartStore((s) => s.add);

  // If the URL carries `?plan=<id>`, pre-fill the cart so a successful auth
  // lands on /app/billing with the plan already added — single-click upgrade
  // from the public catalog.
  const requestedPlanId = params.get('plan');
  useEffect(() => {
    if (!requestedPlanId) return;
    const id = Number(requestedPlanId);
    if (!Number.isFinite(id)) return;
    let alive = true;
    api
      .get<Plan[]>('/api/billing/plans')
      .then(({ data }) => {
        if (!alive) return;
        const plan = data.find((p) => p.id === id);
        if (plan) addToCart(plan);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      alive = false;
    };
  }, [requestedPlanId, addToCart]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (tab === 'register') {
        if (password !== confirm) {
          throw new Error('Las contraseñas no coinciden.');
        }
        const { data } = await api.post<AuthResponse>('/api/auth/register', {
          name,
          email,
          password,
        });
        setSession(data.access_token, data.user);
      } else {
        const { data } = await api.post<AuthResponse>('/api/auth/login', { email, password });
        setSession(data.access_token, data.user);
      }
      // If a plan was requested, send the user to /app/billing so they
      // can complete the purchase immediately; otherwise honor ?next= or
      // fall back to the dashboard.
      const next = params.get('next') || (requestedPlanId ? '/app/billing' : '/app/dashboard');
      navigate(next, { replace: true });
    } catch (err) {
      let msg = tab === 'login' ? 'No se pudo iniciar sesión.' : 'No se pudo crear la cuenta.';
      if (err instanceof Error && !axios.isAxiosError(err)) {
        msg = err.message;
      } else if (axios.isAxiosError(err) && err.response?.data?.detail) {
        const detail = err.response.data.detail;
        msg = typeof detail === 'string' ? detail : msg;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isRegister = tab === 'register';

  return (
    <div
      className="flex min-h-screen items-center justify-center px-6"
      style={{ background: 'var(--surface-base)' }}
    >
      <div
        className="pointer-events-none fixed inset-0"
        style={{
          backgroundImage:
            'linear-gradient(var(--text-muted) 1px, transparent 1px), linear-gradient(to right, var(--text-muted) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          opacity: 0.06,
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <Link to="/" className="no-underline">
            <Brand size="lg" />
          </Link>
          <p className="mt-2 text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Plataforma de cálculo E.060 + ACI 318
          </p>
        </div>

        <div
          className="rounded-[var(--radius-lg)] border shadow-lg"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {/* Tabs */}
          <div className="flex border-b" style={{ borderColor: 'var(--border-subtle)' }}>
            {([
              { id: 'login' as const, label: 'Iniciar sesión' },
              { id: 'register' as const, label: 'Crear cuenta' },
            ]).map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setTab(t.id);
                    setError(null);
                  }}
                  className="flex-1 py-3 text-sm font-medium transition-colors"
                  style={{
                    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
                    borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
                    background: active ? 'var(--surface-1)' : 'transparent',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <form onSubmit={onSubmit} className="p-6">
            <h1 className="mb-1 text-xl" style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em' }}>
              {isRegister ? 'Crear nueva cuenta' : 'Bienvenido de vuelta'}
            </h1>
            <p className="mb-5 text-xs" style={{ color: 'var(--text-muted)' }}>
              {isRegister
                ? 'Tu cuenta tendrá acceso a todos los módulos de cálculo.'
                : 'Ingresa con tu correo y contraseña.'}
            </p>

            {isRegister && (
              <Field icon={UserIcon} label="Nombre completo">
                <input
                  type="text"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  required
                  minLength={2}
                  maxLength={120}
                  className="h-10 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pl-9 pr-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                  placeholder="Ej. Juan Pérez"
                />
              </Field>
            )}

            <Field icon={Mail} label="Correo electrónico">
              <input
                type="email"
                autoFocus={!isRegister}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                className="h-10 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pl-9 pr-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                placeholder="tu@correo.com"
              />
            </Field>

            <Field icon={Lock} label="Contraseña">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                minLength={8}
                className="h-10 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pl-9 pr-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                placeholder={isRegister ? 'Mínimo 8 caracteres' : '••••••••'}
              />
            </Field>

            {isRegister && (
              <Field icon={Lock} label="Confirmar contraseña">
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.currentTarget.value)}
                  required
                  minLength={8}
                  className="h-10 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pl-9 pr-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                  placeholder="Repite la contraseña"
                />
              </Field>
            )}

            {error && (
              <div
                className="mb-4 rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
                style={{ background: 'rgba(239,68,68,0.10)', borderColor: 'var(--danger)', color: 'var(--danger)' }}
              >
                {error}
              </div>
            )}

            <Button type="submit" variant="primary" size="md" className="w-full" disabled={loading}>
              {loading ? (isRegister ? 'Creando cuenta…' : 'Verificando…') : (isRegister ? 'Crear cuenta' : 'Entrar')}
              {!loading && <ArrowRight size={14} strokeWidth={2} />}
            </Button>

            {!isRegister && (
              <div className="mt-4 text-center text-[11px]" style={{ color: 'var(--text-muted)' }}>
                ¿No tienes cuenta?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setTab('register');
                    setError(null);
                  }}
                  className="font-medium underline-offset-2 hover:underline"
                  style={{ color: 'var(--accent)' }}
                >
                  Crea una aquí
                </button>
              </div>
            )}
            {isRegister && (
              <div className="mt-4 text-center text-[11px]" style={{ color: 'var(--text-muted)' }}>
                ¿Ya tienes cuenta?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setTab('login');
                    setError(null);
                  }}
                  className="font-medium underline-offset-2 hover:underline"
                  style={{ color: 'var(--accent)' }}
                >
                  Inicia sesión
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="mt-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
          <Link to="/" className="hover:text-[var(--text-secondary)]" style={{ color: 'var(--text-muted)' }}>
            ← Volver al sitio
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Mail;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      <div className="relative">
        <Icon
          size={14}
          strokeWidth={1.8}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-muted)' }}
        />
        {children}
      </div>
    </label>
  );
}
